import { useEffect, useRef, useState } from 'react';

/**
 * SphereCanvas — glass orb "face": deep-blue glass sphere, two glowing
 * vertical eye lenses, a wave-shaped "mouth" band, and a thin orange
 * spiral/orbit texture across the upper surface (brand accent). Built as
 * an inline SVG (organic curves are far more reliable in SVG than CSS
 * clip-paths) and sized via ResizeObserver off the smaller of the
 * container's own width/height, so it's always a true circle regardless
 * of the tile's aspect ratio.
 *
 * States: idle | speaking | reviewing
 */
export default function SphereCanvas({ sphereState = 'idle' }) {
  const containerRef = useRef(null);
  const [orbSize, setOrbSize] = useState(160);

  // Click-to-wiggle: wiggleKey forces the wiggle element to remount (the
  // only reliable way to *restart* a CSS animation on repeat clicks —
  // toggling a boolean alone wouldn't retrigger it if already true).
  const [wiggleKey, setWiggleKey] = useState(0);
  const [isWiggling, setIsWiggling] = useState(false);
  const wiggleTimeoutRef = useRef(null);

  const handleClick = () => {
    setWiggleKey((k) => k + 1);
    setIsWiggling(true);
    if (wiggleTimeoutRef.current) clearTimeout(wiggleTimeoutRef.current);
    wiggleTimeoutRef.current = setTimeout(() => setIsWiggling(false), 600);
  };

  useEffect(() => () => {
    if (wiggleTimeoutRef.current) clearTimeout(wiggleTimeoutRef.current);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const base = Math.min(rect.width, rect.height);
      setOrbSize(Math.max(90, Math.min(base * 0.62, 260)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const speaking = sphereState === 'speaking';
  const speed = speaking ? '1.6s' : sphereState === 'reviewing' ? '2.8s' : '4.2s';
  const eyeGlow = speaking ? 1 : sphereState === 'reviewing' ? 0.75 : 0.55;
  // Same gentle breathing in every state now — the more exaggerated
  // rise-and-shrink pulse while speaking was too much movement for an
  // interview screen, so it's been dropped in favor of just this.
  const orbAnimation = `botview-orb-breathe ${speed} ease-in-out infinite`;
  // Eyes bob whenever the AI is talking OR right after a click, not just one or the other.
  const eyeAnimation = speaking || isWiggling
    ? `botview-eye-pulse ${speed} ease-in-out infinite, botview-eye-move 0.9s ease-in-out infinite`
    : `botview-eye-pulse ${speed} ease-in-out infinite`;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full rounded-2xl overflow-hidden flex items-center justify-center"
      style={{ background: '#0B0A14' }}
      data-testid="sphere-panel"
    >
      <style>{`
        @keyframes botview-orb-breathe {
          0%, 100% { transform: scale(0.98); }
          50% { transform: scale(1.02); }
        }
        @keyframes botview-eye-pulse {
          0%, 100% { opacity: var(--eye-glow-min); }
          50% { opacity: 1; }
        }
        @keyframes botview-eye-move {
          0%, 100% { transform: translateY(0) scaleY(1); }
          50% { transform: translateY(-2.5px) scaleY(1.12); }
        }
        @keyframes botview-rim-glow {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.4; }
        }
        @keyframes botview-orb-wiggle {
          0% { transform: rotate(0deg); }
          15% { transform: rotate(-9deg); }
          32% { transform: rotate(7deg); }
          48% { transform: rotate(-5deg); }
          64% { transform: rotate(3deg); }
          80% { transform: rotate(-1.5deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>

      {/* Ground shadow */}
      <div
        className="absolute rounded-full"
        style={{
          width: orbSize * 0.9,
          height: orbSize * 0.22,
          bottom: `calc(50% - ${orbSize * 0.62}px)`,
          background: 'radial-gradient(ellipse, rgba(20,40,80,0.55) 0%, rgba(20,40,80,0) 72%)',
          filter: 'blur(6px)',
        }}
      />

      {/* Outer wrapper owns the click-wiggle (rotate); nesting it around the
          scale-animated inner div lets both transforms compose visually
          instead of one CSS animation overriding the other. */}
      <div
        key={wiggleKey}
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
        role="button"
        tabIndex={0}
        aria-label="BotView AI"
        style={{
          width: orbSize,
          height: orbSize,
          cursor: 'pointer',
          animation: isWiggling ? 'botview-orb-wiggle 0.6s ease-in-out 1' : 'none',
        }}
      >
        <div style={{ width: '100%', height: '100%', animation: orbAnimation }}>
          <svg viewBox="0 0 200 200" width="100%" height="100%">
          <defs>
            <radialGradient id="glassRim" cx="38%" cy="30%" r="75%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
              <stop offset="55%" stopColor="rgba(255,255,255,0.08)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
            </radialGradient>
            <radialGradient id="sphereBody" cx="36%" cy="30%" r="80%">
              <stop offset="0%" stopColor="#4a7bb0" />
              <stop offset="30%" stopColor="#1c3355" />
              <stop offset="70%" stopColor="#0c1a30" />
              <stop offset="100%" stopColor="#060d1a" />
            </radialGradient>
            <linearGradient id="waveFill" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#2c5a8a" />
              <stop offset="100%" stopColor="#0a1626" />
            </linearGradient>
            <linearGradient id="eyeFill" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#bfe6ff" />
              <stop offset="100%" stopColor="#4fa8e0" />
            </linearGradient>
            <clipPath id="sphereClip">
              <circle cx="100" cy="100" r="78" />
            </clipPath>
            <filter id="eyeGlow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="3.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Outer glass rim */}
          <circle cx="100" cy="100" r="92" fill="url(#glassRim)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />

          {/* Sphere body */}
          <circle cx="100" cy="100" r="78" fill="url(#sphereBody)" />

          {/* Inner glow within the glass rim/atmosphere band — only while
              the AI is talking. Contained inside the rim, not radiating
              out past the sphere's edge. */}
          {speaking && (
            <circle
              cx="100" cy="100" r="85"
              fill="none"
              stroke="#FF6B1A"
              strokeWidth="10"
              opacity="0.35"
              style={{ animation: 'botview-rim-glow 0.9s ease-in-out infinite' }}
              filter="url(#eyeGlow)"
            />
          )}

          <g clipPath="url(#sphereClip)">
            {/* Orange spiral / orbit texture across the upper surface */}
            <g stroke="#FFC233" fill="none" strokeLinecap="round">
              <path d="M 40 70 A 55 55 0 0 1 150 60" opacity="0.35" strokeWidth="0.8" />
              <path d="M 35 90 A 62 62 0 0 1 158 78" opacity="0.28" strokeWidth="0.7" />
              <path d="M 45 50 A 48 48 0 0 1 140 45" opacity="0.4" strokeWidth="0.9" />
              <path d="M 55 35 A 38 38 0 0 1 130 33" opacity="0.3" strokeWidth="0.6" />
            </g>

            {/* Wave-shaped "mouth" band across the lower sphere */}
            <path
              d="M 22 118 C 55 100, 70 145, 100 128 C 130 111, 148 100, 178 118 L 178 185 L 22 185 Z"
              fill="url(#waveFill)"
              opacity="0.92"
            />
          </g>

          {/* Glowing eyes */}
          <g style={{ '--eye-glow-min': eyeGlow, animation: eyeAnimation }} filter="url(#eyeGlow)">
            <path d="M 80 78 Q 90 92 80 106 Q 70 92 80 78 Z" fill="url(#eyeFill)" />
            <path d="M 118 78 Q 128 92 118 106 Q 108 92 118 78 Z" fill="url(#eyeFill)" />
          </g>

          {/* Glossy highlight */}
          <ellipse cx="72" cy="55" rx="26" ry="14" fill="rgba(255,255,255,0.35)" opacity="0.5" />
        </svg>
        </div>
      </div>

      {/* AI label */}
      <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-lg bg-black/50 backdrop-blur-sm text-xs text-white font-medium z-10">
        BotView AI
        {sphereState === 'speaking' && (
          <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[#FFC233] animate-pulse" />
        )}
      </div>
    </div>
  );
}
